import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../api';
import { logger } from '../../utils/logger';

/* ─── style objects ─── */
const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  padding: '24px 16px',
  maxWidth: '600px',
  margin: '0 auto',
};

const cardStyle: React.CSSProperties = {
  background: 'rgba(13, 20, 40, 0.85)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(212, 175, 55, 0.12)',
  borderRadius: '24px',
  padding: '24px',
  marginBottom: '16px',
  boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
  position: 'relative',
  overflow: 'hidden',
};

const titleStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontStyle: 'italic',
  fontSize: '28px',
  fontWeight: 'bold',
  background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
  backgroundSize: '200% auto',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  marginBottom: '24px',
};

const sectionTitleStyle: React.CSSProperties = {
  color: '#f0f0f0',
  fontSize: '16px',
  fontWeight: '600',
  marginBottom: '16px',
  borderBottom: '1px solid rgba(212, 175, 55, 0.12)',
  paddingBottom: '8px',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 0',
  borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
};

const labelStyle: React.CSSProperties = {
  color: '#f0f0f0',
  fontSize: '14px',
  flex: 1,
};

const descStyle: React.CSSProperties = {
  color: '#b0b0b0',
  fontSize: '12px',
  marginTop: '2px',
};

const backBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid rgba(212, 175, 55, 0.12)',
  color: '#d4af37',
  padding: '8px 16px',
  borderRadius: '12px',
  cursor: 'pointer',
  fontSize: '14px',
  marginBottom: '20px',
};

const unsubscribeBtnStyle: React.CSSProperties = {
  background: 'rgba(220, 53, 69, 0.1)',
  border: '1px solid rgba(220, 53, 69, 0.3)',
  color: '#dc3545',
  padding: '12px 24px',
  borderRadius: '10px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 'bold',
  width: '100%',
  marginTop: '16px',
};

const statusBadgeStyle = (active: boolean): React.CSSProperties => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: '12px',
  fontSize: '11px',
  fontWeight: 'bold',
  background: active ? 'rgba(40, 167, 69, 0.15)' : 'rgba(255, 255, 255, 0.05)',
  color: active ? '#28a745' : '#888',
  marginLeft: '8px',
});

/* ─── Toggle switch component ─── */
interface ToggleProps {
  checked: boolean;
  onChange: (val: boolean) => void;
  label: string;
  description?: string;
}

const Toggle: React.FC<ToggleProps> = ({ checked, onChange, label, description }) => {
  const trackStyle: React.CSSProperties = {
    position: 'relative',
    width: '44px',
    height: '24px',
    borderRadius: '12px',
    background: checked
      ? 'linear-gradient(135deg, #d4af37, #f0d060)'
      : 'rgba(255, 255, 255, 0.1)',
    cursor: 'pointer',
    transition: 'background 0.2s',
    flexShrink: 0,
  };

  const thumbStyle: React.CSSProperties = {
    position: 'absolute',
    top: '2px',
    left: checked ? '22px' : '2px',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    background: checked ? '#1a1a2e' : '#888',
    transition: 'left 0.2s, background 0.2s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
  };

  return (
    <div style={rowStyle}>
      <div style={{ flex: 1 }}>
        <div style={labelStyle}>{label}</div>
        {description && <div style={descStyle}>{description}</div>}
      </div>
      <div style={trackStyle} onClick={() => onChange(!checked)}>
        <div style={thumbStyle} />
      </div>
    </div>
  );
};

const NotificationSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [pushPermission, setPushPermission] = useState<string>('unknown');
  const [forecastReady, setForecastReady] = useState(true);
  const [newAnalysis, setNewAnalysis] = useState(true);
  const [eclipseEvents, setEclipseEvents] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if ('Notification' in window) {
      setPushPermission(Notification.permission);
    }

    // Load saved preferences from localStorage
    const prefs = localStorage.getItem('notification_prefs');
    if (prefs) {
      try {
        const parsed = JSON.parse(prefs);
        setForecastReady(parsed.forecastReady ?? true);
        setNewAnalysis(parsed.newAnalysis ?? true);
        setEclipseEvents(parsed.eclipseEvents ?? true);
      } catch {
        // ignore
      }
    }
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      localStorage.setItem(
        'notification_prefs',
        JSON.stringify({ forecastReady, newAnalysis, eclipseEvents })
      );
      logger.info('Notification preferences saved');
    } catch (error) {
      logger.error({ err: error }, 'Failed to save notification preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleUnsubscribeAll = async () => {
    if (!confirm('Вы уверены, что хотите отписаться от всех уведомлений?')) return;

    try {
      await api.delete('/api/push/unsubscribe');
      setPushPermission('denied');
      logger.info('Unsubscribed from all push notifications');
    } catch (error) {
      logger.error({ err: error }, 'Failed to unsubscribe');
    }
  };

  const handleEnablePush = async () => {
    try {
      const permission = await Notification.requestPermission();
      setPushPermission(permission);

      if (permission === 'granted') {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(
            import.meta.env.VITE_VAPID_PUBLIC_KEY || ''
          ),
        });
        await api.post('/api/push/subscribe', subscription);
        logger.info('Push subscription created');
      }
    } catch (error) {
      logger.error({ err: error }, 'Failed to enable push notifications');
    }
  };

  if (!isAuthenticated) {
    navigate('/');
    return null;
  }

  const isPushActive = pushPermission === 'granted';

  return (
    <div style={{ ...pageStyle, padding: isMobile ? '16px 12px' : '24px 16px', position: 'relative' }}>
      <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
      <button style={backBtnStyle} onClick={() => navigate('/profile')}>
        ← Назад в профиль
      </button>

      <h1 style={{ ...titleStyle, fontSize: isMobile ? '22px' : '28px' }}>Уведомления</h1>

      {/* Push status */}
      <div style={{ ...cardStyle, padding: isMobile ? '16px' : '24px' }}>
        <div style={sectionTitleStyle}>Push-уведомления</div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
          <span style={{ color: '#f0f0f0', fontSize: '14px' }}>Статус:</span>
          <span style={statusBadgeStyle(isPushActive)}>
            {isPushActive ? 'Активны' : pushPermission === 'default' ? 'Не запрошены' : 'Отключены'}
          </span>
        </div>
        {!isPushActive && (
          <button
            style={{
              ...backBtnStyle,
              background: 'linear-gradient(135deg, #d4af37, #f0d060)',
              color: '#1a1a2e',
              fontWeight: 'bold',
              border: 'none',
              width: '100%',
            }}
            onClick={handleEnablePush}
          >
            Включить push-уведомления
          </button>
        )}
      </div>

      {/* Notification types */}
      <div style={{ ...cardStyle, padding: isMobile ? '16px' : '24px' }}>
        <div style={sectionTitleStyle}>Типы уведомлений</div>

        <Toggle
          checked={forecastReady}
          onChange={setForecastReady}
          label="Готовность прогнозов"
          description="Уведомление, когда ваш прогноз готов"
        />

        <Toggle
          checked={newAnalysis}
          onChange={setNewAnalysis}
          label="Новые анализы"
          description="Уведомления о доступности новых видов анализа"
        />

        <Toggle
          checked={eclipseEvents}
          onChange={setEclipseEvents}
          label="Затмения и важные события"
          description="Напоминания о предстоящих затмениях"
        />
      </div>

      {/* Save button */}
      <button
        style={{
          ...backBtnStyle,
          background: 'linear-gradient(135deg, #d4af37, #f0d060)',
          color: '#1a1a2e',
          fontWeight: 'bold',
          border: 'none',
          width: '100%',
          opacity: saving ? 0.6 : 1,
        }}
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? 'Сохранение...' : 'Сохранить настройки'}
      </button>

      {/* Unsubscribe all */}
      {isPushActive && (
        <button style={unsubscribeBtnStyle} onClick={handleUnsubscribeAll}>
          Отписаться от всех уведомлений
        </button>
      )}
    </div>
  );
};

// Helper: convert VAPID public key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default NotificationSettingsPage;
